
import { Component, OnInit, inject, signal, viewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { finalize } from 'rxjs';
import { CommonRoute } from '../../../../../common/enums/common-route.enum';
import { AuthenticationService } from '../../../../../common/services/authentication/authentication.service';
import { LayoutService } from '../../../../layout/service/app.layout.service';

// Added for Captcha
import { MessageService } from 'primeng/api';
import { CaptchaComponent } from '../../../../../common/components/captcha/captcha.component';
import { ComponentAccess } from '../../../../../common/decorators/component-access.decorator';
import { CaptchaMessage } from '../../../../../common/enums/captcha-message.enum';
import { passwordMatchValidator } from '../../../../../common/validators/password-match.validator';
import { passwordStrengthValidator } from '../../../../../common/validators/password-strength.validator';
import { AppConfigService } from '../../../../../common/config/app.config.service';

@Component({
  selector: 'app-register',
  standalone: true,
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
  imports: [
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    CardModule,
    StyleClassModule,
    RippleModule,
    CaptchaComponent
],
})
@ComponentAccess('RegistrationComponent')
export class RegistrationComponent implements OnInit {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  readonly layoutService = inject(LayoutService);
  private readonly configService = inject(AppConfigService);
  private readonly messageService = inject(MessageService);

  registerForm: FormGroup<{
    email: FormControl<string | null>;
    password: FormControl<string | null>;
    confirmPassword: FormControl<string | null>;
  }>;
  readonly registrationFailed = signal<boolean>(false);
  /** From the moment Sign Up is clicked: the browser check alone can take seconds. */
  readonly isSubmitting = signal<boolean>(false);

  // Captcha related properties
  readonly isCaptchaRequired = signal<boolean>(false);
  private readonly captcha = viewChild(CaptchaComponent);

  constructor() {
    this.registerForm = new FormGroup(
      {
        email: new FormControl('', [Validators.required, Validators.email]),
        password: new FormControl('', [
          Validators.required,
          passwordStrengthValidator(),
        ]),
        confirmPassword: new FormControl('', [Validators.required]),
      },
      { validators: passwordMatchValidator },
    );
  }
  ngOnInit(): void {
    if (!this.configService.isRegistrationAllowed) {
      this.router.navigate([CommonRoute.NOT_FOUND]);
      return;
    }

    this.isCaptchaRequired.set(this.configService.isCaptchaEnabled);
  }

  async onSubmit(): Promise<void> {
    if (this.registerForm.valid && !this.isSubmitting()) {
      // Busy BEFORE the browser check, which can take seconds on its own.
      this.isSubmitting.set(true);

      // The captcha challenge runs on demand here (execute-on-submit), so
      // nothing refreshes or goes stale while the page sits open.
      const captchaOk = (await this.captcha()?.verify()) ?? false;
      if (this.isCaptchaRequired() && !captchaOk) {
        this.isSubmitting.set(false);
        this.messageService.add({
          severity: 'error',
          summary: CaptchaMessage.FailedSummary,
          detail: CaptchaMessage.Failed,
        });
        return;
      }

      const { email, password } = this.registerForm.value;
      if (!email || !password) {
        this.isSubmitting.set(false);
        return;
      }
      this.authenticationService
        .register(email, password)
        .pipe(finalize(() => this.isSubmitting.set(false)))
        .subscribe((success) => {
          if (success) {
            this.router.navigate([CommonRoute.LOGIN]);
          } else {
            this.registrationFailed.set(true);
          }
        });
    }
  }

  navigateToLogin(): void {
    this.router.navigate([CommonRoute.LOGIN]);
  }

  emailErrorText(): string | null {
    const control = this.registerForm.get('email');
    if (control && control.invalid && (control.touched || control.dirty)) {
      if (control.errors?.['required']) {
        return 'Email is required.';
      }
      if (control.errors?.['email']) {
        return 'Invalid email address.';
      }
    }
    return null;
  }

  passwordErrorText(): string | null {
    const control = this.registerForm.get('password');
    if (control && control.invalid && (control.touched || control.dirty)) {
      if (control.errors?.['required']) {
        return 'Password is required.';
      }
      if (control.errors?.['passwordStrength']) {
        const strengthErrors = control.errors['passwordStrength'];
        const message = 'Password must ';
        const parts = [];
        if (strengthErrors.hasMinLength) {
          parts.push('be at least 8 characters long');
        }
        const detailParts = [];
        if (strengthErrors.hasUpperCase) {
          detailParts.push('an uppercase letter');
        }
        if (strengthErrors.hasLowerCase) {
          detailParts.push('a lowercase letter');
        }
        if (strengthErrors.hasNumeric) {
          detailParts.push('a number');
        }
        if (strengthErrors.hasSpecial) {
          detailParts.push('a special character');
        }
        if (detailParts.length > 0) {
          parts.push(`contain ${detailParts.join(', ')}`);
        }
        return message + parts.join(' and ') + '.';
      }
    }
    return null;
  }

  confirmPasswordErrorText(): string | null {
    const confirmPasswordControl = this.registerForm.get('confirmPassword');

    if (
      confirmPasswordControl &&
      (confirmPasswordControl.touched || confirmPasswordControl.dirty)
    ) {
      if (confirmPasswordControl.errors?.['required']) {
        return 'Confirm password is required.';
      }
    }
    if (
      this.registerForm.errors?.['passwordMismatch'] &&
      (confirmPasswordControl?.touched || confirmPasswordControl?.dirty)
    ) {
      return 'Passwords do not match.';
    }
    if (confirmPasswordControl?.errors?.['passwordMismatch']) {
      return 'Passwords do not match.';
    }

    return null;
  }
}
